import { useRef, useEffect } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { removeCustomPantryItem } from './pantry.service'
import { showUndoToast } from '@/shared/utils/toast'
import type { Ingredient } from './types'

export function useRemoveCustomItem() {
  const queryClient = useQueryClient()
  const { t } = useTranslation('pantry')
  const timersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map())
  const rejectsRef = useRef<Map<string, (reason?: unknown) => void>>(new Map())

  useEffect(() => {
    const timers = timersRef.current
    return () => {
      timers.forEach((timer) => clearTimeout(timer))
      timers.clear()
      rejectsRef.current.clear()
    }
  }, [])

  return useMutation({
    mutationFn: (id: string) =>
      new Promise<void>((resolve, reject) => {
        rejectsRef.current.set(id, reject)
        timersRef.current.set(
          id,
          setTimeout(async () => {
            try {
              await removeCustomPantryItem(id)
              resolve()
            } catch (err) {
              reject(err)
            } finally {
              timersRef.current.delete(id)
              rejectsRef.current.delete(id)
            }
          }, 3000),
        )
      }),
    onMutate: async (id: string) => {
      await queryClient.cancelQueries({ queryKey: ['pantry', 'ingredients'] })
      const prev = queryClient.getQueryData<Ingredient[]>(['pantry', 'ingredients'])
      queryClient.setQueryData<Ingredient[]>(['pantry', 'ingredients'], (old) =>
        old?.filter((ing) => ing.id !== id) ?? [],
      )
      showUndoToast(
        t('essentials.removeSuccess'),
        t('shell.toast.undo', { ns: 'shell' }),
        () => {
          const timer = timersRef.current.get(id)
          if (timer) clearTimeout(timer)
          timersRef.current.delete(id)
          rejectsRef.current.get(id)?.(new Error('undone'))
          rejectsRef.current.delete(id)
        },
      )
      return { prev }
    },
    onError: (_err, _id, ctx) => {
      queryClient.setQueryData(['pantry', 'ingredients'], ctx?.prev)
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['pantry', 'ingredients'] })
    },
  })
}
